import { Close } from '@mui/icons-material';
import { Box, Button, IconButton, Link, Paper, Popover, Stack, TextField, Typography } from '@mui/material';
import { produce } from 'immer';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { DashboardContext } from '../../../contexts/DashboardContext';
import { GroupSettingsContext } from '../../../contexts/GroupSettingsContext';
import useDashboardQuery from '../../../hooks/useDashboardQuery';
import { updateDashboardFunc } from '../../../lib/helper';
import { AnswerQuestionResponse, AnswerQuestionResponseType } from '../../../types/search';
import ObjectUtils from '../../../utils/ObjectUtils';
import MultiSelector from '../../atoms/MultiSelector';
import QueryBlock from '../../molecules/dashboard-details-v2/QueryBlock';
import QueryInline from '../../molecules/dashboard-details-v2/QueryInline';

const UrlRegex = /^(https?:\/\/)?(www\.)?([^\\/]+)(\/.*)?$/;
const LinkedInRegex = /linkedin\.com\/(company|showcase)\/([^\\/?]+)/;

const CompanyDetailsCard: React.FC<{}> = () => {
    const { tagGroups } = useContext(GroupSettingsContext);
    const { dashboard } = useContext(DashboardContext);
    const { queryAnswer: { answeredQuestion: { answer: description } }, updateQuery: updateDescription } = useDashboardQuery('Description');
    const { queryAnswer: { answeredQuestion: { answer: founded } }, updateQuery: updateFounded } = useDashboardQuery('Founded');
    const { queryAnswer: { answeredQuestion: { answer: headcount } }, updateQuery: updateHeadcount  } = useDashboardQuery('Headcount');
    const { queryAnswer: { answeredQuestion: { answer: location } }, updateQuery: updateLocation  } = useDashboardQuery('Location');
    const { queryAnswer: { answeredQuestion: { answer: website } }, updateQuery: updateWebsite  } = useDashboardQuery('Website');
    const [editQueryProps, setEditField] = useState<{
        anchor: HTMLElement | null,
        field: string,
        value: string,
        updater: (answer: AnswerQuestionResponse) => void
    }>({ anchor: null, field: '', value: '', updater: () => {} });
    const [updating, setUpdating] = useState<boolean>(false);

    const tagGroup = useMemo(() => tagGroups?.find(({ title }) => title.toLowerCase() === 'sectors'), [tagGroups]);

    const getDisplayLink = useCallback(() => {
        const checkUrl = website as string;
        const link = !checkUrl?.includes('https') && !checkUrl?.includes('http') ? `https://${checkUrl}` : checkUrl;
        const url = link.replace('https://', '').replace('http://', '');
        let display = website;

        if (LinkedInRegex.test(url)) {
            const matches = url.match(LinkedInRegex);

            display = !!matches ? `/${matches.pop()}` : display;
        } else if (UrlRegex.test(url)) {
            display = url.match(UrlRegex)?.[3] || display;
        }

        return display;
    }, [website]);

    const lookUpTagsSelection = useCallback(() =>
      dashboard?.tagGroups?.find(({ title }) => title.toLowerCase() === 'sectors')?.tags || []
    , [dashboard]);

    const handleTextEdit = useCallback((field: string, value: string, updater: ((answer: AnswerQuestionResponse) => void)) => {
        setEditField({ anchor: document.activeElement as HTMLElement, field, value, updater });
    }, []);

    const handleResetQueryUpdate = useCallback(() => {
        setEditField({ anchor: null, field: '', value: '', updater: () => {} });
    }, []);

    const handleQueryUpdate = useCallback((e: any) => {
        e.stopPropagation();
        e.preventDefault();
        editQueryProps.updater({
            answer: editQueryProps.value,
            type: AnswerQuestionResponseType.TEXT,
        });
        handleResetQueryUpdate();
    // eslint-disable-next-line
    }, [editQueryProps]);

    const handleSelectTags = useCallback(async (selections: string[]) => {
        const title = 'Sectors';
        const lookUpTagGroup = dashboard?.tagGroups?.find(dashboard => dashboard.title.toLowerCase() === title.toLowerCase());

        if (!ObjectUtils.equalObjects(lookUpTagGroup?.tags, selections)) {
            setUpdating(true);
            await updateDashboardFunc(produce(dashboard!, (draft) => {
                if (!draft.tagGroups?.find(group => group.title.toLowerCase() === title.toLowerCase())) {
                    draft.tagGroups = [...(draft.tagGroups || []), { title, tags: selections }]
                        .map(({ title, tags }) => ({title, tags }));
                } else {
                    const groupIndex = draft.tagGroups.findIndex(group => group.title.toLowerCase() === title.toLowerCase());

                    draft.tagGroups[groupIndex].tags = selections;
                    draft.tagGroups = draft.tagGroups.map(({ title, tags }) => ({title, tags }));
                }
            })).then(() => setUpdating(false));
        }
    }, [dashboard]);

    return (<>
        <Paper elevation={0} sx={{ p: 3, borderRadius: 1 }}>
            <Box mb={2}>
                <Typography fontSize={16} fontWeight={600} color="#2c3e50">
                    {'Company Details'}
                </Typography>
            </Box>
            <QueryBlock
                value={description as string}
                onEdit={() => handleTextEdit('Description', description as string, updateDescription)}
                editable />
            <QueryInline
                label="Founded"
                value={founded as string}
                onEdit={() => handleTextEdit('Founded', founded as string, updateFounded)}
                editable />
            <QueryInline
                label="Employees"
                value={headcount as string}
                onEdit={() => handleTextEdit('Headcount', headcount as string, updateHeadcount)}
                editable />
            <QueryInline
                label="Location"
                value={location as string}
                onEdit={() => handleTextEdit('Location', location as string, updateLocation)}
                editable />
            <QueryInline
                label="Website"
                value={<Link href={website as string}>{getDisplayLink()}</Link>}
                onEdit={() => handleTextEdit('Website', website as string, updateWebsite)}
                editable />
            <QueryInline
                label="Sectors"
                value={
                    <MultiSelector
                        mainOptions={tagGroup?.tags || []}
                        selections={lookUpTagsSelection()}
                        onSelection={(selections) => handleSelectTags(selections)}
                        loading={updating}
                        limit={tagGroup?.tagsPerDashboard || 3}
                        withSearch
                        fullSpan />
                } />
        </Paper>
        <Popover
            open={Boolean(editQueryProps.anchor)}
            anchorEl={editQueryProps.anchor}
            onClose={handleResetQueryUpdate}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <Box sx={{ width: 350, p: 2, borderRadius: 8 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography fontSize={14} fontWeight={600}>
                        {`Edit ${editQueryProps.field}`}
                    </Typography>
                    <IconButton size="small" onClick={handleResetQueryUpdate}>
                        <Close sx={{ fontSize: 16 }} />
                    </IconButton>
                </Stack>
                <TextField value={editQueryProps.value}
                    rows={editQueryProps.field === 'Description' ? 5 : undefined}
                    onChange={(e) => setEditField(prev => ({ ...prev, value: e.target.value }))}
                    fullWidth multiline />
                <Stack direction="row" spacing={1} justifyContent="flex-end" mt={2}>
                    <Button size="small" onClick={handleResetQueryUpdate}>
                        {'Cancel'}
                    </Button>
                    <Button size="small" variant="contained" onClick={handleQueryUpdate}>
                        {'Save'}
                    </Button>
                </Stack>
            </Box>
        </Popover>
    </>);
};

export default CompanyDetailsCard;